import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { getScheduleData } from '../utils/chartAPI';

const round2 = (v) => (v == null ? null : +parseFloat(v).toFixed(2));


function sumByTimestamp(scheduleObj) {
  const series = scheduleObj?.timeseries ?? [];
  return series.reduce((acc, r) => {
    (r?.schedule ?? []).forEach((p) => {
      const tsMs = dayjs(p.time).valueOf();
      acc[tsMs] = (acc[tsMs] ?? 0) + (p?.value ?? 0);
    });
    return acc;
  }, {});
}

export default function useORScheduleData({
  site,
  msSessionInfo,
  startDate,
  endDate,
  inView,
  refreshKey,
}) {
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const hasFetched = useRef(false);

  const effStart = startDate ? dayjs(startDate) : dayjs().startOf('day');
  let effEnd = endDate ? dayjs(endDate) : effStart.add(1, 'day').subtract(1, 'minute');

  if (!effEnd.isAfter(effStart)) {
    effEnd = effStart.add(1, 'minute');
  }


  useEffect(() => {
    hasFetched.current = false;
  }, [refreshKey, site?.id, effStart.valueOf(), effEnd.valueOf()]);

  useEffect(() => {
    if (!inView || !site?.id || !msSessionInfo) return;
    if (refreshKey === undefined && hasFetched.current) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const da = await getScheduleData(msSessionInfo, site, effStart, effEnd, 'dayahead', '10S');
        const pd = await getScheduleData(msSessionInfo, site, effStart, effEnd, 'predispatch', '10S');

        const daObj = sumByTimestamp(da);
        const pdObj = sumByTimestamp(pd);

        const daPts = Object.keys(daObj)
          .map(Number)
          .sort((a, b) => a - b)
          .map((ts) => ({ x: ts, y: round2(daObj[ts]) }));

        const pdPts = Object.keys(pdObj)
          .map(Number)
          .sort((a, b) => a - b)
          .map((ts) => ({ x: ts, y: round2(pdObj[ts]) }));

        setSeries([
          { name: 'Day Ahead', type: 'bar', data: daPts },
          { name: 'Predispatch', type: 'bar', data: pdPts },
        ]);

        hasFetched.current = true;
      } catch (err) {
        console.error('OR Schedule fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [inView, site?.id, msSessionInfo, effStart.valueOf(), effEnd.valueOf(), refreshKey]);

  return { loading, series };
}
